import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PrePracticeScreen from 'Components/ExerciseSection/PrePracticeScreen';
import type { RootState } from 'Reducers';
import { hydrateRequested, selectExerciseCells } from 'Reducers/exerciseProgressReducer';
import { getExercisePrePracticeReadModel } from './prePractice.fixture';
import { deriveTierProgress } from './deriveTierProgress';
import './ExercisePrePractice.css';

/**
 * Route container for the Exercises section pre-practice screen (MUS-312 / MUS-480).
 *
 * The exercise STRUCTURE (levels / tiers / cells) is still fixture-sourced
 * (Decision #131.1); MUS-480 swaps only the `progress` source from the static
 * fixture to the live per-cell frontier slice (MUS-318). It hydrates that slice on
 * mount, reads it through the slice's thin selector, and derives the per-tier grip
 * with `deriveTierProgress` (the #117.5 waypoint-domination read). The screen is
 * unchanged - still props-typed on the read model, and it does not know a fixture
 * or a slice exists.
 *
 * The route is registered flag-gated on USE_EXERCISES in `App.tsx`, so this mounts
 * only where the section is enabled (never in production, where the flag is
 * force-off), and the hydrate saga is itself dark behind the same flag. The
 * internal PageViewEvent for '/exercises' is emitted by `App.tsx`'s per-navigation
 * effect (MUS-309); nothing is dispatched here for analytics.
 */
const ExercisePrePractice: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Structure is a pure fixture read - resolve it once.
  const readModel = React.useMemo(() => getExercisePrePracticeReadModel(), []);
  const { exerciseId } = readModel;

  // Tap-to-play (MUS-481): entering the real play route for the tapped level/tier.
  // The play screen resolves the level's representative cell + the tier's waypoint;
  // a missing tier defaults to the level's first tier at that surface.
  const onSelectLevel = React.useCallback(
    (levelNumber: number, tierNumber?: number) => {
      const tierSegment = tierNumber ?? 1;
      navigate(`/exercises/play/${levelNumber}/${tierSegment}`);
    },
    [navigate],
  );

  // Hydrate the per-cell frontier on mount (server-authoritative; App.tsx does NOT
  // dispatch a hydrate for '/exercises', so the container owns it). Re-runs only if
  // the exercise id changes. Dark behind USE_EXERCISES via the saga's own guard.
  React.useEffect(() => {
    dispatch(hydrateRequested({ exerciseId }));
  }, [dispatch, exerciseId]);

  const cellsByKey = useSelector((s: RootState) => selectExerciseCells(s, exerciseId));

  // Overlay live grip on the fixture structure. Memoized on the hydrated cells so a
  // re-derive happens only when the frontier actually changes.
  const progress = React.useMemo(
    () => deriveTierProgress(readModel, cellsByKey),
    [readModel, cellsByKey],
  );

  return (
    <div className="exercise-prepractice-page">
      <PrePracticeScreen readModel={{ ...readModel, progress }} onSelectLevel={onSelectLevel} />
    </div>
  );
};

export default ExercisePrePractice;
